// Core packages
import { CollectionViewer } from '@angular/cdk/collections';
import { DataSource } from '@angular/cdk/table';

// Third party packages
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

// Custom packages
import { DeadlinesService } from 'src/app/modules/deadlines/deadlines.service';
import { Deadline } from 'src/app/shared/models/deadline/deadline.model';
import { HelperService } from 'src/app/shared/services/helper.service';

/**
 * @see https://blog.angular-university.io/angular-material-data-table/
 *
 * NB
 * If you need to change this to make a new DataSource
 * just change the following:
 *
 * - Change The class name in the first line
 * - Change DataSource<T> (use your model type) in first line
 * - Change BehaviourSubject<T> in the second line
 * - Change the service inside the class constructor
 * - Change the Observable<T> used in the connect() line
 * - Change the service name inside loadItems()
 */
export class DeadlinesDataSource implements DataSource<Deadline> {
  private itemsSubject$ = new BehaviorSubject<Deadline[]>([]);
  private totalCountSubject$ = new BehaviorSubject<number>(0);
  private loadingSubject$ = new BehaviorSubject<boolean>(true);

  public loading$ = this.loadingSubject$.asObservable();
  public totalCount$ = this.totalCountSubject$.asObservable();

  /**
   * Class constructor
   */
  constructor(
    private deadlinesService: DeadlinesService,
    private helperService: HelperService,
    private translateService: TranslateService,
  ) { }

  /**
   * Connect data table to itemsSubject$
   *
   * @since 1.0.0
   *
   * @param collectionViewer collection viewer object
   * @returns Observable
   */
  connect(collectionViewer: CollectionViewer): Observable<Deadline[]> {
    return this.itemsSubject$.asObservable();
  }

  /**
   * Disconnect data table from subjects
   *
   * @since 1.0.0
   *
   * @param collectionViewer collection viewer object
   */
  disconnect(collectionViewer: CollectionViewer): void {
    this.itemsSubject$.complete();
    this.totalCountSubject$.complete();
    this.loadingSubject$.complete();
  }

  /**
   * Load items from back-end using service injected in the constructor
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering objects with filtering data
   */
  loadSubscriptionsItems(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): void {
    this.loadingSubject$.next(true); // Start loading
    this.deadlinesService.getSubscriptionsList(start, limit, sort, sortVersus, search)
      .pipe(
        catchError(err => { // If errors, return empty array
          this.helperService.handleError(err);
          return of({ status: true, data: { items: [], totalCount: 0 } });
        }),
        finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
      )
      .subscribe((result: any) => {
        if (result.data.items && Array.isArray(result.data.items)) {
          result.data.items.forEach((item: any) => {
            item.subscription.forfait = "No";
            if (item.subscription.flatRate || item.subscription.flatRateScan) {
              item.subscription.forfait = "Sì";
            }
            item.subscription.options = "";
            if (item.subscription.safetyQrCode && item.subscription.nfcAccess) {
              if (item.dossier) {
                item.subscription.options = this.translateService.instant("deadlines.subscriptionsList.options.all");
              } else {
                item.subscription.options = this.translateService.instant("deadlines.subscriptionsList.options.qrcodeNfc");
              }
            } else if (item.subscription.safetyQrCode && !item.subscription.nfcAccess) {
              item.subscription.options = this.translateService.instant("deadlines.subscriptionsList.options.qrcode");
              if (item.dossier) {
                item.subscription.options = this.translateService.instant("deadlines.subscriptionsList.options.dossierQrcode");
              } else {
                item.subscription.options = this.translateService.instant("deadlines.subscriptionsList.options.qrcode");
              }
            } else if (!item.subscription.safetyQrCode && item.subscription.nfcAccess) {
              if (item.dossier) {
                item.subscription.options = this.translateService.instant("deadlines.subscriptionsList.options.dossierNfc");
              } else {
                item.subscription.options = this.translateService.instant("deadlines.subscriptionsList.options.nfc");
              }
            } else {
              if (item.dossier) {
                item.subscription.options = this.translateService.instant("deadlines.subscriptionsList.options.dossier");
              }
            }
          });
        }
        this.itemsSubject$.next(result.data.items);
        this.totalCountSubject$.next(result.data.totalCount);
      });
  }

  /**
   * Load items from back-end using service injected in the constructor
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering objects with filtering data
   */
  loadDocumentsItems(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): void {
    this.loadingSubject$.next(true); // Start loading
    this.deadlinesService.getDocumentsList(start, limit, sort, sortVersus, search)
      .pipe(
        catchError(err => { // If errors, return empty array
          this.helperService.handleError(err);
          return of({ status: true, data: { items: [], totalCount: 0 } });
        }),
        finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
      )
      .subscribe((result: any) => {
        this.itemsSubject$.next(result.data.items);
        this.totalCountSubject$.next(result.data.totalCount);
      });
  }

  /**
   * Load items of property from back-end using service injected in the constructor
   *
   * @since 1.0.0
   *
   * @param [propertyId] Query left offset
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering objects with filtering data
   */
  loadDocumentsPoperty(propertyId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): void {
    this.loadingSubject$.next(true); // Start loading
    this.deadlinesService.getDocumentsPopertyList(propertyId, start, limit, sort, sortVersus, search)
      .pipe(
        catchError(err => { // If errors, return empty array
          this.helperService.handleError(err);
          return of({ status: true, data: { items: [], totalCount: 0 } });
        }),
        finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
      )
      .subscribe((result: any) => {
        this.itemsSubject$.next(result.data.items);
        this.totalCountSubject$.next(result.data.totalCount);
      });
  }
  
  /**
   * Load items from back-end using service injected in the constructor
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering objects with filtering data
   */
  loadSubscriptionsSuppliersItems(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): void {
    this.loadingSubject$.next(true); // Start loading
    this.deadlinesService.getSubscriptionsSuppliersList(start, limit, sort, sortVersus, search)
      .pipe(
        catchError(err => { // If errors, return empty array
          this.helperService.handleError(err);
          return of({ status: true, data: { items: [], totalCount: 0 } });
        }),
        finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
      )
      .subscribe((result: any) => {
        if (result.data.items && Array.isArray(result.data.items)) {
          result.data.items.forEach((item: any) => {
            item.subscription.forfait = "No";
            if (item.subscription.flatRate || item.subscription.flatRateScan) {
              item.subscription.forfait = "Sì";
            }
            item.subscription.options = "";
            if (item.subscription.safetyQrCode && item.subscription.nfcAccess) {
              if (item.dossier) {
                item.subscription.options = this.translateService.instant("deadlines.subscriptionsList.options.all");
              } else {
                item.subscription.options = this.translateService.instant("deadlines.subscriptionsList.options.qrcodeNfc");
              }
            } else if (item.subscription.safetyQrCode && !item.subscription.nfcAccess) {
              item.subscription.options = this.translateService.instant("deadlines.subscriptionsList.options.qrcode");
              if (item.dossier) {
                item.subscription.options = this.translateService.instant("deadlines.subscriptionsList.options.dossierQrcode");
              } else {
                item.subscription.options = this.translateService.instant("deadlines.subscriptionsList.options.qrcode");
              }
            } else if (!item.subscription.safetyQrCode && item.subscription.nfcAccess) {
              if (item.dossier) {
                item.subscription.options = this.translateService.instant("deadlines.subscriptionsList.options.dossierNfc");
              } else {
                item.subscription.options = this.translateService.instant("deadlines.subscriptionsList.options.nfc");
              }
            } else {
              if (item.dossier) {
                item.subscription.options = this.translateService.instant("deadlines.subscriptionsList.options.dossier");
              }
            }
          });
        }
        this.itemsSubject$.next(result.data.items);
        this.totalCountSubject$.next(result.data.totalCount);
      });
  }

  
  /**
   * Load items from back-end using service injected in the constructor
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering objects with filtering data
   */
  loadNfcAccessSubscriptionsItems(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): void {
    this.loadingSubject$.next(true); // Start loading
    this.deadlinesService.getNfcAccessSubscriptionsList(start, limit, sort, sortVersus, search)
      .pipe(
        catchError(err => { // If errors, return empty array
          this.helperService.handleError(err);
          return of({ status: true, data: { items: [], totalCount: 0 } });
        }),
        finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
      )
      .subscribe((result: any) => {
        this.itemsSubject$.next(result.data.items);
        this.totalCountSubject$.next(result.data.totalCount);
      });
  }

  /**
   * Load items from back-end using service injected in the constructor
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering objects with filtering data
   */
  loadSubscriptionsPrivacyItems(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): void {
    this.loadingSubject$.next(true); // Start loading
    this.deadlinesService.getSubscriptionsPrivacyList(start, limit, sort, sortVersus, search)
      .pipe(
        catchError(err => { // If errors, return empty array
          this.helperService.handleError(err);
          return of({ status: true, data: { items: [], totalCount: 0 } });
        }),
        finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
      )
      .subscribe((result: any) => {
        this.itemsSubject$.next(result.data.items);
        this.totalCountSubject$.next(result.data.totalCount);
      });
  }

  /**
   * Load items from back-end using service injected in the constructor
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering objects with filtering data
   */
  loadSubscriptionsPrivacySagItems(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): void {
    this.loadingSubject$.next(true); // Start loading
    this.deadlinesService.getSubscriptionsPrivacySagList(start, limit, sort, sortVersus, search)
      .pipe(
        catchError(err => { // If errors, return empty array
          this.helperService.handleError(err);
          return of({ status: true, data: { items: [], totalCount: 0 } });
        }),
        finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
      )
      .subscribe((result: any) => {
        this.itemsSubject$.next(result.data.items);
        this.totalCountSubject$.next(result.data.totalCount);
      });
  }
}
